import type React from 'react'
import { useEffect, useRef } from 'react'
import { Microphone, Send2 } from 'iconsax-react'
import type { Status } from './chatMachine'
import { InterruptButton } from './InterruptButton'
import { StatusLine } from './StatusLine'

export function TextComposer({
    input,
    setInput,
    status,
    isReplying,
    statusText,
    onSend,
    onInterrupt,
    onEnterVoice,
}: {
    input: string
    setInput: (v: string) => void
    status: Status
    isReplying: boolean
    statusText: string
    onSend: () => void
    onInterrupt: () => void
    onEnterVoice: () => void
}) {
    const taRef = useRef<HTMLTextAreaElement>(null)

    // 自动撑高:单行=48(h-12,和按钮齐平),多行向上长,封顶 128(约 5 行)后内部滚动。
    // +2 抵消 border-box 下 1px 上下边框,避免单/多行差 2px。
    useEffect(() => {
        const ta = taRef.current
        if (!ta) return
        ta.style.height = 'auto'
        ta.style.height = `${Math.min(Math.max(ta.scrollHeight + 2, 48), 128)}px`
    }, [input])

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        // Enter 发送,Shift+Enter 换行
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onSend()
        }
    }

    return (
        <div className="hairline-t relative px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <StatusLine text={statusText} active={status !== 'idle'} listening={false} capturing={false} danger={false} />
            <div className="flex items-end gap-2.5">
                <textarea
                    ref={taRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="输入消息"
                    disabled={isReplying}
                    rows={1}
                    className="no-scrollbar glass max-h-32 flex-1 resize-none overflow-y-auto rounded-2xl bg-bg-secondary px-4 py-2.5 text-[15px] leading-6 text-text-primary transition placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
                />
                {isReplying ? (
                    <InterruptButton onClick={onInterrupt} />
                ) : input.trim() ? (
                    <button
                        onClick={onSend}
                        className="liquid flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-md shadow-accent/30 transition active:scale-95"
                        aria-label="发送"
                    >
                        <Send2 size={29} color="currentColor" variant="Linear" />
                    </button>
                ) : (
                    <button
                        onClick={onEnterVoice}
                        className="glass flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-text-primary transition active:scale-95"
                        aria-label="语音通话"
                    >
                        <Microphone size={27} color="currentColor" variant="Linear" />
                    </button>
                )}
            </div>
        </div>
    )
}
